import React from 'react';

import ReactDOM from 'react-dom/client';

import {
	AppRoot as AppRootShared,
	ErrorBoundary as ErrorBoundaryShared,
} from '@bcp-channels-sharedcomponents-reactjs/core';
import {
	CoreHelper,
	LoggerHelper,
	MapAppMobileHandlers,
	NativeCommunicationHelper,
	TranslationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { Company, Language, Platform } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { ThemeProvider as ThemeProviderShared } from '@bcp-channels-sharedcomponents-reactjs/theme/dist/providers';
import { GlobalStyle, ThemeProvider as ThemeProviderAccolade } from '@bcp-nextgen-dx-component-factory/design-system';
import { Accolade } from '@bcp-nextgen-dx-component-factory/theme-accolade';
import { ApiConfig, LoggerConfig } from '@configs';
import { defaultI18n } from '@i18n';
import {
	BaseStateSessionInfo,
	BaseStateSessionInfoCompany,
	BaseStateSessionInfoFlow,
	BaseStateSessionInfoGeoId,
} from '@store';

import packageJson from '../package.json';
import { DebugSettings } from './components/debugSettings';
import { GlobalLoader } from './components/globalLoader';
import { UnexpectedError } from './components/unexpectedError';
import { FetchToken } from './fetchToken';
import { getSavedTokenDataFromStorage, getSessionInfo, saveTokenDataFromStorage } from './helpers/sessionHelper';
import './index.css';
import { TouchpointFrameworkBootstrap } from './touchpointFrameworkBootstrap';
import { Anything } from './types/common';

// to fix not exists children prop in react-18
const AppRoot = AppRootShared as any;
const ThemeProvider = ThemeProviderShared as any;
const ErrorBoundary = ErrorBoundaryShared as any;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let started = false;

const language = getSavedTokenDataFromStorage('languageCode') as string;
let languageCode = Language.pt;

switch (language.toUpperCase()) {
	case 'ES':
		languageCode = Language.es;
		break;
	case 'EN':
		languageCode = Language.en;
		break;
	case 'FR':
		languageCode = Language.fr;
		break;
	default:
		languageCode = Language.pt;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const startSimulator = (sessionInfoPassed: string, targetElement = document.getElementById('root')): void => {
	started = true;

	if (sessionInfoPassed) {
		const sessionInfo: BaseStateSessionInfo = getSessionInfo(sessionInfoPassed);

		LoggerHelper.init(LoggerConfig, { all: true, flow: true, core: true, sessionInfo: true });

		TranslationHelper.register(
			'resources.global',
			{
				default: { ab: defaultI18n.pt, bcp: defaultI18n.pt },
				en: { ab: defaultI18n.en, bcp: defaultI18n.en },
				pt: { ab: defaultI18n.pt, bcp: defaultI18n.pt },
				es: { ab: defaultI18n.es, bcp: defaultI18n.es },
			},
			{ addAndReplace: true }
		);

		const children = (
			<AppRoot
				globalData={{
					company: sessionInfo.company?.toLowerCase() === 'ab' ? Company.ab : Company.bcp,
					language: languageCode,
					platform: Platform.mobile,
				}}
			>
				<ThemeProviderAccolade
					theme={Accolade}
					locale={{
						default: 'pt-BR',
					}}
				>
					<GlobalStyle />
					<ThemeProvider>
						<DebugSettings>
							<GlobalLoader />
							<ErrorBoundary
								containerErrorMessage={(error: Anything): React.ReactNode => (
									<UnexpectedError error={error} />
								)}
							>
								<FetchToken>
									<TouchpointFrameworkBootstrap sessionInfo={sessionInfo} />
								</FetchToken>
							</ErrorBoundary>
						</DebugSettings>
					</ThemeProvider>
				</ThemeProviderAccolade>
			</AppRoot>
		);

		ReactDOM.createRoot(targetElement as HTMLElement).render(
			process.env.NODE_ENV === 'production' ? <React.StrictMode>{children}</React.StrictMode> : children
		);
	} else {
		ReactDOM.createRoot(targetElement as HTMLElement).render(<span>Session Info not found...</span>);
	}
};

const url = new URL(window.location.href);
const returnUrl = url.searchParams.get('returnUrl');
const ott = url.searchParams.get('ott');
const tokenOAuth = url.searchParams.get('tokenOAuth');
const state = url.searchParams.get('state');

const savedTokenOAuth = getSavedTokenDataFromStorage<string>('tokenOAuth');

// cSpell: enable
// comment to run mocks

returnUrl && saveTokenDataFromStorage('returnUrl', returnUrl);
ott && saveTokenDataFromStorage('ott', ott);
tokenOAuth && saveTokenDataFromStorage('tokenOAuth', tokenOAuth);
state && saveTokenDataFromStorage('state', decodeURIComponent(state));

if (window.location.pathname === '/') {
	window.history.replaceState(undefined, '', window.location.origin);
}

// cSpell: disable
const sessionInfo: BaseStateSessionInfo = {
	accountList: [],
	alias: '',
	appMobile: false,
	company: BaseStateSessionInfoCompany.BCP,
	flow: BaseStateSessionInfoFlow.Home,
	geoId: BaseStateSessionInfoGeoId.Lisbon,
	interactionId: '',
	language: languageCode.toUpperCase(),
	mobileSystem: '',
	ocpApimSubscriptionKey: '536285b3e29b4bcf9702f6e74fb572e8',
	sessionId: '',
	targetInfrastructure: '21',
	targetSystem: 'PTCorporate',
	tokenOAuth: tokenOAuth || savedTokenOAuth || '',
};

startSimulator(JSON.stringify(sessionInfo));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as Anything).startSimulator = startSimulator;

export const mapAppMobileHandlers: MapAppMobileHandlers = {
	startSimulator,
};

NativeCommunicationHelper.mapAppMobileHandlers(mapAppMobileHandlers);

(window as Anything).startJourney = startSimulator;

(window as Anything).reactApp = {
	version: packageJson.version,
	config: {
		api: ApiConfig,
		logger: LoggerConfig,
	},
};
